import { useCallback, useMemo, useState } from 'react'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { openShaderCodeEditor } from '@/components/canvas/shader/open-code-editor'
import { getSketchById } from '@/lib/canvas/shader/library'
import { mergeParamLabels, parseParams } from '@/lib/canvas/shader/params'
import { RUNTIME_TEMPLATES } from '@/lib/canvas/shader/runtime-templates'
import { CollapsibleSection, SelectParam } from './settings-sections'
import { ShaderParamControls } from './shader/ShaderParamControls'
import { ShaderReplaceDialog, type ShaderReplaceChoice } from './shader/ShaderReplaceDialog'
import { ShaderProviderSettings } from '@/components/canvas/shader/ShaderProviderSettings'
import { ShaderInputToggles } from '@/components/canvas/shader/ShaderInputChips'
import { ShaderPromptHistory } from '@/components/canvas/shader/ShaderPromptHistory'
import { restoreShaderRevision, shaderStateInputTypes } from '@/components/canvas/shader/generation-state'
import { resolveShaderProviderConfig } from '@/components/canvas/shader/node-provider'
import { useShaderGenerationScope } from '@/components/canvas/shader/ShaderGenerationScope'
import { useWiredShaderInputs } from '@/components/canvas/shader/wired-inputs'
import type { ShaderInputType } from '@/lib/canvas/shader/media/contract'
import type { ExtractedParam, RuntimeMode } from '@/lib/canvas/shader/types'
import type { ShaderPromptEntry, ShaderProviderConfig } from '@/lib/canvas/shader/authoring/types'

export interface ShaderNodeSettingsProps {
  nodeId: string
  state: Record<string, unknown>
  onStateChange: (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void
}

type State = Record<string, unknown>

const RUNTIME_OPTIONS: { value: RuntimeMode; label: string }[] = [
  { value: 'glsl', label: 'GLSL (fragment shader)' },
  { value: 'p5', label: 'p5.js' },
  { value: 'threejs', label: 'Three.js' },
  { value: 'raw', label: 'Raw JavaScript' },
]

function runtimeLabel(mode: RuntimeMode): string {
  return RUNTIME_OPTIONS.find((o) => o.value === mode)?.label ?? mode
}

/** Code the user wrote or changed, as opposed to an untouched library sketch or starter. */
function isCustomCode(state: State): boolean {
  const code = typeof state.code === 'string' ? state.code : ''
  if (!code.trim()) return false
  const sketch = typeof state.sketchId === 'string' ? getSketchById(state.sketchId) : undefined
  if (sketch?.code === code) return false
  return !Object.values(RUNTIME_TEMPLATES).includes(code)
}

function withStarter(prev: State, mode: RuntimeMode): State {
  const sketch = typeof prev.sketchId === 'string' ? getSketchById(prev.sketchId) : undefined
  return {
    ...prev,
    sketchId: undefined,
    // Drop a title that only named the replaced sketch; keep one the user chose.
    title: sketch && prev.title === sketch.name ? undefined : prev.title,
    code: RUNTIME_TEMPLATES[mode],
    runtimeMode: mode,
    // Starters declare no inputs of their own; the node falls back to the image input.
    inputTypes: undefined,
    extractedParams: [],
    paramValues: {},
    generation: undefined,
  }
}

export function ShaderNodeSettings({ nodeId, state, onStateChange }: ShaderNodeSettingsProps) {
  const [pendingMode, setPendingMode] = useState<RuntimeMode | null>(null)

  const runtimeMode = (state.runtimeMode as RuntimeMode | undefined) ?? 'glsl'
  const code = typeof state.code === 'string' ? state.code : ''
  const paramValues = (state.paramValues as Record<string, unknown> | undefined) ?? {}
  const previousParams = state.extractedParams as ExtractedParam[] | undefined
  const params = useMemo(() => mergeParamLabels(parseParams(code), previousParams), [code, previousParams])
  const storedProvider = state.provider
  const provider = useMemo(() => resolveShaderProviderConfig(storedProvider), [storedProvider])
  const inputTypes = shaderStateInputTypes(state)
  const wiredInputs = useWiredShaderInputs(useShaderGenerationScope(), nodeId)
  const history = Array.isArray(state.promptHistory) ? (state.promptHistory as ShaderPromptEntry[]) : []

  const handleProviderChange = useCallback((next: ShaderProviderConfig) => {
    onStateChange((prev) => ({ ...prev, provider: next }))
  }, [onStateChange])

  const handleInputsChange = useCallback((next: ShaderInputType[]) => {
    onStateChange((prev) => ({ ...prev, inputTypes: next }))
  }, [onStateChange])

  const handleReusePrompt = useCallback((prompt: string) => {
    onStateChange((prev) => ({ ...prev, prompt }))
  }, [onStateChange])

  const handleRestore = useCallback((entry: ShaderPromptEntry) => {
    onStateChange((prev) => restoreShaderRevision(prev, entry))
  }, [onStateChange])

  const handleRuntimeChange = useCallback((value: string) => {
    const mode = value as RuntimeMode
    if (mode === runtimeMode) return
    if (isCustomCode(state)) setPendingMode(mode)
    else onStateChange((prev) => withStarter(prev, mode))
  }, [state, runtimeMode, onStateChange])

  const handleParamChange = useCallback((name: string, value: number | string | boolean) => {
    onStateChange((prev) => ({ ...prev, paramValues: { ...((prev.paramValues as State | undefined) ?? {}), [name]: value } }))
  }, [onStateChange])

  const dialog = useMemo(() => {
    if (!pendingMode) return null
    const close = () => setPendingMode(null)
    return {
      title: `Switch to ${runtimeLabel(pendingMode)}?`,
      description: `Your code is written for ${runtimeLabel(runtimeMode)} and won't run as ${runtimeLabel(pendingMode)}. Start from the ${runtimeLabel(pendingMode)} starter, or keep your code and convert it yourself.`,
      choices: [
        { label: 'Use starter code', onSelect: () => { onStateChange((prev) => withStarter(prev, pendingMode)); close() } },
        { label: 'Keep my code', onSelect: () => { onStateChange((prev) => ({ ...prev, runtimeMode: pendingMode, sketchId: undefined })); close() } },
      ] satisfies ShaderReplaceChoice[],
    }
  }, [pendingMode, runtimeMode, onStateChange])

  return (
    <div>
      <CollapsibleSection title="Write with AI" defaultOpen>
        <ShaderProviderSettings config={provider} onChange={handleProviderChange} />
      </CollapsibleSection>

      <CollapsibleSection title="Inputs" defaultOpen>
        <div className="flex w-[336px] flex-col gap-1.5 px-2 py-2">
          {code.trim() ? (
            <>
              <ShaderInputToggles selected={inputTypes} connected={wiredInputs} onChange={handleInputsChange} />
              <p className="text-[10px] text-muted-foreground">Each input gets a handle on the node. Wired inputs keep theirs.</p>
            </>
          ) : (
            // An empty node offers every handle, so there is nothing to toggle yet.
            <p className="text-[10px] text-muted-foreground">
              Every input has a handle until the shader is written. Then the node keeps the inputs its code reads.
            </p>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Code" defaultOpen>
        <div className="flex w-[336px] flex-col gap-2 py-2">
          <SelectParam label="Runtime" value={runtimeMode} onChange={handleRuntimeChange} options={RUNTIME_OPTIONS} placeholder="Runtime" />
          <Button size="sm" variant="outline" onClick={() => openShaderCodeEditor(nodeId)} className="h-8 w-full gap-1.5">
            <Icon name="code" className="h-3.5 w-3.5" />
            {code ? 'Edit code' : 'Write code'}
          </Button>
          {code && (
            <p className="px-2 text-[10px] text-muted-foreground">
              {code.split('\n').length} lines · {code.length.toLocaleString()} chars
            </p>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Parameters" defaultOpen>
        <ShaderParamControls params={params} values={paramValues} onChange={handleParamChange} />
      </CollapsibleSection>

      <CollapsibleSection title="Recent prompts" defaultOpen={history.length > 0}>
        <ShaderPromptHistory entries={history} onReuse={handleReusePrompt} onRestore={handleRestore} currentCode={code} />
      </CollapsibleSection>

      <ShaderReplaceDialog
        open={dialog !== null}
        title={dialog?.title ?? ''}
        description={dialog?.description ?? ''}
        choices={dialog?.choices ?? []}
        onCancel={() => setPendingMode(null)}
      />
    </div>
  )
}
